import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  Member,
  PaginatedResponse,
  PlayEvent,
  PlayJoinPolicy,
  PlayerRatingSettings,
  StudentProfile,
} from '@court-sync/shared';
import { formatPlayerRating, participantTypesForJoinPolicy } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { Loader2, User, UserCheck, UserPlus } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Drawer,
  Field,
  FieldLabel,
  FieldDescription,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import type { PaginationState } from '@/components/ui';
import { clubMembersService } from '@/services/clubMembers';
import { isApiError } from '@/services/apiClient';
import { playEventService } from '@/services/playEvent';
import { playerRatingService } from '@/services/playerRating';
import { academyStudentService } from '@/services/academyStudentService';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';

type ParticipantTab = 'member' | 'student' | 'guest';
type ParticipantGender = 'male' | 'female' | 'other';

const PAGE_SIZE = 20;

const TAB_LABELS: Record<ParticipantTab, string> = {
  member: 'Club member',
  student: 'Academy student',
  guest: 'Guest',
};

function tabsForJoinPolicy(joinPolicy: PlayJoinPolicy): ParticipantTab[] {
  return participantTypesForJoinPolicy(joinPolicy);
}


function isDuplicateParticipantError(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.status === 409 || /already registered/i.test(error.message))
  );
}

function isPaginatedMembersResponse(
  res: { success: boolean; data?: Member[]; pagination?: PaginationState }
): res is PaginatedResponse<Member> {
  return res.success && res.pagination != null;
}

interface DuplicateParticipantNotice {
  tab: 'member' | 'student';
  id: string;
  name: string;
}

interface PlayEventAddParticipantDrawerProps {
  organizationId: string;
  clubId: string;
  academyId: string | null | undefined;
  event: PlayEvent;
  registeredMemberIds: string[];
  registeredStudentIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

interface ParticipantBrowsePanelProps<T> {
  title: string;
  rows: T[];
  pagination: PaginationState | null;
  loading: boolean;
  onPageChange: (page: number) => void;
  emptyMessage: string;
  renderRow: (item: T) => ReactNode;
  getRowKey: (item: T) => string;
  bulkToolbar?: ReactNode;
}

function ParticipantBrowsePanel<T>({
  title,
  rows,
  pagination,
  loading,
  onPageChange,
  emptyMessage,
  renderRow,
  getRowKey,
  bulkToolbar,
}: ParticipantBrowsePanelProps<T>) {
  const from = pagination ? (pagination.page - 1) * pagination.limit + 1 : 1;
  const to = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : rows.length;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="flex min-w-0 items-center gap-2 font-medium text-gray-900 dark:text-white">
          <User className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{title}</span>
        </h4>
        {pagination && pagination.total > 0 ? (
          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {pagination.totalPages > 1
              ? `${from}–${to} of ${pagination.total}`
              : `${pagination.total} total`}
          </span>
        ) : null}
      </div>

      {bulkToolbar}

      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
          {emptyMessage}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {rows.map((item) => (
              <li key={getRowKey(item)} className="px-3 py-2.5">
                {renderRow(item)}
              </li>
            ))}
          </ul>
          {pagination && pagination.totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

export function PlayEventAddParticipantDrawer({
  organizationId,
  clubId,
  academyId,
  event,
  registeredMemberIds: registeredMemberIdList,
  registeredStudentIds: registeredStudentIdList,
  isOpen,
  onClose,
  onAdded,
}: PlayEventAddParticipantDrawerProps) {
  const allowedTabs = useMemo(() => tabsForJoinPolicy(event.joinPolicy), [event.joinPolicy]);
  const participantTabs = useMemo(
    () =>
      allowedTabs.map((tab) => ({
        id: tab,
        label: TAB_LABELS[tab],
        mobileLabel:
          tab === 'member' ? 'Member' : tab === 'student' ? 'Student' : 'Guest',
      })),
    [allowedTabs]
  );
  const registeredMemberIds = useMemo(
    () => new Set(registeredMemberIdList),
    [registeredMemberIdList]
  );
  const registeredStudentIds = useMemo(
    () => new Set(registeredStudentIdList),
    [registeredStudentIdList]
  );
  const [activeTab, setActiveTab] = useState<ParticipantTab>(allowedTabs[0] ?? 'member');
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [comped, setComped] = useState(false);

  const [memberPage, setMemberPage] = useState(1);
  const [memberRows, setMemberRows] = useState<Member[]>([]);
  const [memberPagination, setMemberPagination] = useState<PaginationState | null>(null);
  const [memberListLoading, setMemberListLoading] = useState(false);

  const [studentPage, setStudentPage] = useState(1);
  const [studentRows, setStudentRows] = useState<StudentProfile[]>([]);
  const [studentPagination, setStudentPagination] = useState<PaginationState | null>(null);
  const [studentListLoading, setStudentListLoading] = useState(false);

  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestGender, setGuestGender] = useState<'' | ParticipantGender>('');
  const [guestPlayerRating, setGuestPlayerRating] = useState('');
  const [ratingSettings, setRatingSettings] = useState<PlayerRatingSettings | null>(null);
  const [memberRatingsById, setMemberRatingsById] = useState<Map<string, number>>(new Map());
  const [studentRatingsById, setStudentRatingsById] = useState<Map<string, number>>(new Map());
  const [pendingMember, setPendingMember] = useState<Member | null>(null);
  const [pendingMemberGender, setPendingMemberGender] = useState<'' | ParticipantGender>('');
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateParticipantNotice | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<Map<string, Member>>(new Map());
  const [selectedStudents, setSelectedStudents] = useState<Map<string, StudentProfile>>(new Map());

  const resetForm = useCallback(() => {
    setSearch('');
    setSearchQuery('');
    setComped(false);
    setMemberPage(1);
    setMemberRows([]);
    setMemberPagination(null);
    setStudentPage(1);
    setStudentRows([]);
    setStudentPagination(null);
    setGuestFirstName('');
    setGuestLastName('');
    setGuestEmail('');
    setGuestPhone('');
    setGuestGender('');
    setGuestPlayerRating('');
    setRatingSettings(null);
    setMemberRatingsById(new Map());
    setStudentRatingsById(new Map());
    setPendingMember(null);
    setPendingMemberGender('');
    setDuplicateNotice(null);
    setSelectedMembers(new Map());
    setSelectedStudents(new Map());
    setActiveTab(allowedTabs[0] ?? 'member');
  }, [allowedTabs]);

  useEffect(() => {
    if (!isOpen) return;
    void playerRatingService.getSettings(organizationId).then((res) => {
      if (res.success && res.data) {
        setRatingSettings(res.data);
      }
    });
  }, [isOpen, organizationId]);

  const eventSportId = event.academySportId ?? null;
  const ratingsEnabled = ratingSettings?.enabled === true && eventSportId != null;

  useEffect(() => {
    if (!isOpen || !ratingsEnabled || memberRows.length === 0) {
      setMemberRatingsById(new Map());
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        memberRows.map(async (member) => {
          const res = await playerRatingService.getMemberSportRatings(clubId, member.id);
          const rating = res.data?.find((row) => row.organizationSportId === eventSportId)?.rating;
          return rating != null ? ([member.id, rating] as const) : null;
        })
      );
      if (cancelled) return;
      setMemberRatingsById(new Map(entries.filter((entry): entry is [string, number] => entry != null)));
    })();

    return () => {
      cancelled = true;
    };
  }, [clubId, eventSportId, isOpen, memberRows, ratingsEnabled]);

  useEffect(() => {
    if (!isOpen || !ratingsEnabled || !academyId || studentRows.length === 0) {
      setStudentRatingsById(new Map());
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        studentRows.map(async (student) => {
          const res = await playerRatingService.getStudentSportRatings(academyId, student.id);
          const rating = res.data?.find((row) => row.organizationSportId === eventSportId)?.rating;
          return rating != null ? ([student.id, rating] as const) : null;
        })
      );
      if (cancelled) return;
      setStudentRatingsById(new Map(entries.filter((entry): entry is [string, number] => entry != null)));
    })();

    return () => {
      cancelled = true;
    };
  }, [academyId, eventSportId, isOpen, ratingsEnabled, studentRows]);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setMemberPage(1);
    setStudentPage(1);
  }, [searchQuery]);

  const loadMembersPage = useCallback(
    async (page: number) => {
      setMemberListLoading(true);
      try {
        const res = await clubMembersService.listMembers(clubId, {
          page,
          limit: PAGE_SIZE,
          search: searchQuery || undefined,
          isActive: true,
        });
        if (isPaginatedMembersResponse(res)) {
          setMemberRows(res.data ?? []);
          setMemberPagination(res.pagination);
        } else if (res.success && res.data) {
          setMemberRows(res.data);
          setMemberPagination({
            page: 1,
            limit: res.data.length,
            total: res.data.length,
            totalPages: 1,
          });
        } else {
          setMemberRows([]);
          setMemberPagination(null);
        }
      } finally {
        setMemberListLoading(false);
      }
    },
    [clubId, searchQuery]
  );

  const loadStudentsPage = useCallback(
    async (page: number) => {
      if (!academyId) return;
      setStudentListLoading(true);
      try {
        const res = await academyStudentService.getAcademyStudentsPaginated(academyId, page, PAGE_SIZE, {
          searchQuery: searchQuery || undefined,
          isActive: true,
        });
        setStudentRows(res.data ?? []);
        setStudentPagination(res.pagination ?? null);
      } catch {
        setStudentRows([]);
        setStudentPagination(null);
      } finally {
        setStudentListLoading(false);
      }
    },
    [academyId, searchQuery]
  );

  useEffect(() => {
    if (!isOpen || activeTab !== 'member') return;
    void loadMembersPage(memberPage);
  }, [activeTab, isOpen, loadMembersPage, memberPage]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'student' || !academyId) return;
    void loadStudentsPage(studentPage);
  }, [academyId, activeTab, isOpen, loadStudentsPage, studentPage]);

  useEffect(() => {
    setDuplicateNotice(null);
  }, [search, activeTab]);

  const handleTabChange = useCallback((tab: ParticipantTab) => {
    setActiveTab(tab);
    setSearch('');
    setSearchQuery('');
    setMemberPage(1);
    setStudentPage(1);
    setMemberRows([]);
    setStudentRows([]);
    setPendingMember(null);
    setPendingMemberGender('');
    setDuplicateNotice(null);
    setSelectedMembers(new Map());
    setSelectedStudents(new Map());
  }, []);

  const showDuplicateNotice = useCallback((notice: DuplicateParticipantNotice) => {
    setDuplicateNotice(notice);
  }, []);

  const toggleMemberSelection = useCallback((member: Member) => {
    setSelectedMembers((prev) => {
      const next = new Map(prev);
      if (next.has(member.id)) {
        next.delete(member.id);
      } else {
        next.set(member.id, member);
      }
      return next;
    });
  }, []);

  const toggleStudentSelection = useCallback((student: StudentProfile) => {
    setSelectedStudents((prev) => {
      const next = new Map(prev);
      if (next.has(student.id)) {
        next.delete(student.id);
      } else {
        next.set(student.id, student);
      }
      return next;
    });
  }, []);

  const eligibleMembersOnPage = useMemo(
    () => memberRows.filter((member) => !registeredMemberIds.has(member.id)),
    [memberRows, registeredMemberIds]
  );

  const eligibleStudentsOnPage = useMemo(
    () => studentRows.filter((student) => !registeredStudentIds.has(student.id)),
    [registeredStudentIds, studentRows]
  );

  const allEligibleMembersOnPageSelected = useMemo(
    () =>
      eligibleMembersOnPage.length > 0 &&
      eligibleMembersOnPage.every((member) => selectedMembers.has(member.id)),
    [eligibleMembersOnPage, selectedMembers]
  );

  const allEligibleStudentsOnPageSelected = useMemo(
    () =>
      eligibleStudentsOnPage.length > 0 &&
      eligibleStudentsOnPage.every((student) => selectedStudents.has(student.id)),
    [eligibleStudentsOnPage, selectedStudents]
  );

  const toggleSelectAllMembersOnPage = useCallback(() => {
    setSelectedMembers((prev) => {
      const next = new Map(prev);
      if (allEligibleMembersOnPageSelected) {
        for (const member of eligibleMembersOnPage) {
          next.delete(member.id);
        }
      } else {
        for (const member of eligibleMembersOnPage) {
          next.set(member.id, member);
        }
      }
      return next;
    });
  }, [allEligibleMembersOnPageSelected, eligibleMembersOnPage]);

  const toggleSelectAllStudentsOnPage = useCallback(() => {
    setSelectedStudents((prev) => {
      const next = new Map(prev);
      if (allEligibleStudentsOnPageSelected) {
        for (const student of eligibleStudentsOnPage) {
          next.delete(student.id);
        }
      } else {
        for (const student of eligibleStudentsOnPage) {
          next.set(student.id, student);
        }
      }
      return next;
    });
  }, [allEligibleStudentsOnPageSelected, eligibleStudentsOnPage]);

  const addMemberWithGender = async (member: Member, gender: ParticipantGender) => {
    const displayName = `${member.firstName} ${member.lastName}`.trim();
    if (registeredMemberIds.has(member.id)) {
      showDuplicateNotice({ tab: 'member', id: member.id, name: displayName });
      return;
    }

    setDuplicateNotice(null);
    setSubmitting(true);
    try {
      const res = await playEventService.addParticipant(organizationId, event.id, {
        participantType: 'member',
        memberId: member.id,
        gender,
      }, comped);
      if (res.success) {
        toast.success(`${displayName} added`);
        onAdded();
        onClose();
        return;
      }
      toast.error(res.error ?? 'Failed to add member');
    } catch (error) {
      if (isDuplicateParticipantError(error)) {
        showDuplicateNotice({ tab: 'member', id: member.id, name: displayName });
        return;
      }
      toast.error(isApiError(error) ? error.message : 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMember = (member: Member) => {
    if (registeredMemberIds.has(member.id)) {
      showDuplicateNotice({
        tab: 'member',
        id: member.id,
        name: `${member.firstName} ${member.lastName}`.trim(),
      });
      return;
    }
    if (member.gender) {
      void addMemberWithGender(member, member.gender);
      return;
    }
    setPendingMember(member);
    setPendingMemberGender('');
  };

  const handleConfirmPendingMember = () => {
    if (!pendingMember) return;
    if (!pendingMemberGender) {
      toast.error('Gender is required');
      return;
    }
    void addMemberWithGender(pendingMember, pendingMemberGender);
  };

  const handleAddStudent = async (student: StudentProfile) => {
    const displayName = `${student.firstName} ${student.lastName}`.trim();
    if (registeredStudentIds.has(student.id)) {
      showDuplicateNotice({ tab: 'student', id: student.id, name: displayName });
      return;
    }

    setDuplicateNotice(null);
    setSubmitting(true);
    try {
      const res = await playEventService.addParticipant(organizationId, event.id, {
        participantType: 'student',
        studentId: student.id,
        gender: student.gender,
        skillLevel: student.skillLevel,
        dateOfBirth: student.dateOfBirth ?? undefined,
      }, comped);
      if (res.success) {
        toast.success(`${displayName} added`);
        onAdded();
        onClose();
        return;
      }
      toast.error(res.error ?? 'Failed to add student');
    } catch (error) {
      if (isDuplicateParticipantError(error)) {
        showDuplicateNotice({ tab: 'student', id: student.id, name: displayName });
        return;
      }
      toast.error(isApiError(error) ? error.message : 'Failed to add student');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkAddMembers = async () => {
    const members = [...selectedMembers.values()];
    if (members.length === 0) return;

    const withoutGender = members.filter((member) => !member.gender);
    const withGender = members.filter((member) => member.gender);
    if (withGender.length === 0) {
      toast.error('Selected members need gender on file — add them individually.');
      return;
    }

    setSubmitting(true);
    setDuplicateNotice(null);
    let added = 0;
    const failed: string[] = [];

    for (const member of withGender) {
      const displayName = `${member.firstName} ${member.lastName}`.trim();
      if (registeredMemberIds.has(member.id)) {
        continue;
      }
      try {
        const res = await playEventService.addParticipant(
          organizationId,
          event.id,
          {
            participantType: 'member',
            memberId: member.id,
            gender: member.gender!,
          },
          comped
        );
        if (res.success) {
          added += 1;
          setSelectedMembers((prev) => {
            const next = new Map(prev);
            next.delete(member.id);
            return next;
          });
        } else {
          failed.push(displayName);
        }
      } catch (error) {
        if (isDuplicateParticipantError(error)) {
          continue;
        }
        failed.push(displayName);
      }
    }

    setSubmitting(false);

    if (added > 0) {
      onAdded();
    }

    if (added > 0 && failed.length === 0 && withoutGender.length === 0) {
      toast.success(`Added ${added} member${added === 1 ? '' : 's'}`);
    } else if (added > 0) {
      const parts = [`Added ${added} member${added === 1 ? '' : 's'}`];
      if (withoutGender.length > 0) {
        parts.push(
          `${withoutGender.length} skipped (gender required — add individually)`
        );
      }
      if (failed.length > 0) {
        parts.push(`${failed.length} failed`);
      }
      toast.success(parts.join('. '));
    } else if (withoutGender.length > 0) {
      toast.error('Selected members need gender on file — add them individually.');
    } else {
      toast.error('Could not add selected members');
    }
  };

  const handleBulkAddStudents = async () => {
    const students = [...selectedStudents.values()];
    if (students.length === 0) return;

    const withoutGender = students.filter((student) => !student.gender);
    const withGender = students.filter((student) => student.gender);
    if (withGender.length === 0) {
      toast.error('Selected students need gender on file — add them individually.');
      return;
    }

    setSubmitting(true);
    setDuplicateNotice(null);
    let added = 0;
    const failed: string[] = [];

    for (const student of withGender) {
      const displayName = `${student.firstName} ${student.lastName}`.trim();
      if (registeredStudentIds.has(student.id)) {
        continue;
      }
      try {
        const res = await playEventService.addParticipant(
          organizationId,
          event.id,
          {
            participantType: 'student',
            studentId: student.id,
            gender: student.gender,
            skillLevel: student.skillLevel,
            dateOfBirth: student.dateOfBirth ?? undefined,
          },
          comped
        );
        if (res.success) {
          added += 1;
          setSelectedStudents((prev) => {
            const next = new Map(prev);
            next.delete(student.id);
            return next;
          });
        } else {
          failed.push(displayName);
        }
      } catch (error) {
        if (isDuplicateParticipantError(error)) {
          continue;
        }
        failed.push(displayName);
      }
    }

    setSubmitting(false);

    if (added > 0) {
      onAdded();
    }

    if (added > 0 && failed.length === 0 && withoutGender.length === 0) {
      toast.success(`Added ${added} student${added === 1 ? '' : 's'}`);
    } else if (added > 0) {
      const parts = [`Added ${added} student${added === 1 ? '' : 's'}`];
      if (withoutGender.length > 0) {
        parts.push(
          `${withoutGender.length} skipped (gender required — add individually)`
        );
      }
      if (failed.length > 0) {
        parts.push(`${failed.length} failed`);
      }
      toast.success(parts.join('. '));
    } else if (withoutGender.length > 0) {
      toast.error('Selected students need gender on file — add them individually.');
    } else {
      toast.error('Could not add selected students');
    }
  };

  const handleAddGuest = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    if (!guestFirstName.trim() || !guestLastName.trim()) {
      toast.error('Guest name is required');
      return;
    }
    if (!guestEmail.trim() && !guestPhone.trim()) {
      toast.error('Guest email or phone is required');
      return;
    }
    if (!guestGender) {
      toast.error('Gender is required');
      return;
    }

    setSubmitting(true);
    try {
      const parsedGuestRating = guestPlayerRating.trim() ? Number(guestPlayerRating) : undefined;
      const res = await playEventService.addParticipant(organizationId, event.id, {
        participantType: 'guest',
        guestFirstName: guestFirstName.trim(),
        guestLastName: guestLastName.trim(),
        contactEmail: guestEmail.trim() || undefined,
        contactPhone: guestPhone.trim() || undefined,
        gender: guestGender,
        playerRating:
          parsedGuestRating != null && Number.isFinite(parsedGuestRating)
            ? parsedGuestRating
            : undefined,
      }, comped);
      if (res.success) {
        toast.success('Guest added');
        onAdded();
        onClose();
        return;
      }
      toast.error(res.error ?? 'Failed to add guest');
    } finally {
      setSubmitting(false);
    }
  };

  const showCompedToggle =
    event.paymentMode === 'at_door' || event.paymentMode === 'online_on_join';

  const renderMemberRow = (member: Member) => {
    const memberName = `${member.firstName} ${member.lastName}`.trim();
    const alreadyAdded = registeredMemberIds.has(member.id);
    const isSelected = selectedMembers.has(member.id);
    const showDuplicateAlert =
      duplicateNotice?.tab === 'member' && duplicateNotice.id === member.id;
    const memberRating = memberRatingsById.get(member.id);

    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            {!alreadyAdded ? (
              <Checkbox
                checked={isSelected}
                className="mt-0.5 shrink-0"
                disabled={submitting}
                onCheckedChange={() => toggleMemberSelection(member)}
                aria-label={`Select ${memberName}`}
              />
            ) : null}
            <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium text-gray-900 dark:text-white">{memberName}</span>
              {member.email ? (
                <span className="w-full truncate text-xs text-gray-500 dark:text-gray-400">
                  {member.email}
                </span>
              ) : null}
              {!alreadyAdded && !member.gender ? (
                <span className="w-full text-xs text-amber-700 dark:text-amber-300">
                  Gender required — add individually
                </span>
              ) : null}
              {ratingsEnabled && memberRating != null ? (
                <span className="w-full text-xs text-gray-500 dark:text-gray-400">
                  {ratingSettings?.label}: {formatPlayerRating(memberRating)}
                </span>
              ) : null}
            </div>
          </div>
          {alreadyAdded ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              <UserCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
              Already added
            </span>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 gap-1 px-2 text-xs"
              isLoading={submitting && pendingMember?.id !== member.id && !isSelected}
              disabled={(submitting && pendingMember?.id === member.id) || isSelected}
              onClick={() => handleAddMember(member)}
            >
              <UserPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Add
            </Button>
          )}
        </div>
        {showDuplicateAlert ? (
          <Alert variant="warning" className="mt-2 py-2">
            <AlertDescription>
              <strong>{duplicateNotice.name}</strong> is already on this event&apos;s roster.
              Pick someone else or close this drawer to review the roster.
            </AlertDescription>
          </Alert>
        ) : null}
      </>
    );
  };

  const renderStudentRow = (student: StudentProfile) => {
    const studentName = `${student.firstName} ${student.lastName}`.trim();
    const alreadyAdded = registeredStudentIds.has(student.id);
    const isSelected = selectedStudents.has(student.id);
    const showDuplicateAlert =
      duplicateNotice?.tab === 'student' && duplicateNotice.id === student.id;
    const studentRating = studentRatingsById.get(student.id);

    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            {!alreadyAdded ? (
              <Checkbox
                checked={isSelected}
                className="mt-0.5 shrink-0"
                disabled={submitting}
                onCheckedChange={() => toggleStudentSelection(student)}
                aria-label={`Select ${studentName}`}
              />
            ) : null}
            <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium text-gray-900 dark:text-white">{studentName}</span>
              {student.skillLevel ? (
                <span className="text-xs capitalize text-gray-500 dark:text-gray-400">
                  {student.skillLevel}
                </span>
              ) : null}
              {ratingsEnabled && studentRating != null ? (
                <span className="w-full text-xs text-gray-500 dark:text-gray-400">
                  {ratingSettings?.label}: {formatPlayerRating(studentRating)}
                </span>
              ) : null}
            </div>
          </div>
          {alreadyAdded ? (
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              <UserCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
              Already added
            </span>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 gap-1 px-2 text-xs"
              isLoading={submitting && !isSelected}
              disabled={submitting || isSelected}
              onClick={() => void handleAddStudent(student)}
            >
              <UserPlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Add
            </Button>
          )}
        </div>
        {showDuplicateAlert ? (
          <Alert variant="warning" className="mt-2 py-2">
            <AlertDescription>
              <strong>{duplicateNotice.name}</strong> is already on this event&apos;s roster.
              Pick someone else or close this drawer to review the roster.
            </AlertDescription>
          </Alert>
        ) : null}
      </>
    );
  };

  const browseLoading = activeTab === 'member' ? memberListLoading : studentListLoading;
  const selectedBulkCount =
    activeTab === 'member' ? selectedMembers.size : activeTab === 'student' ? selectedStudents.size : 0;
  const bulkAddEligibleCount =
    activeTab === 'member'
      ? [...selectedMembers.values()].filter((member) => member.gender).length
      : selectedStudents.size;

  const memberBulkToolbar =
    eligibleMembersOnPage.length > 0 ? (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          {selectedMembers.size > 0
            ? `${selectedMembers.size} selected`
            : 'Select members to add in bulk'}
        </span>
        <button
          type="button"
          className="shrink-0 font-medium text-primary hover:underline"
          onClick={toggleSelectAllMembersOnPage}
        >
          {allEligibleMembersOnPageSelected ? 'Clear page' : 'Select page'}
        </button>
      </div>
    ) : null;

  const studentBulkToolbar =
    eligibleStudentsOnPage.length > 0 ? (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          {selectedStudents.size > 0
            ? `${selectedStudents.size} selected`
            : 'Select students to add in bulk'}
        </span>
        <button
          type="button"
          className="shrink-0 font-medium text-primary hover:underline"
          onClick={toggleSelectAllStudentsOnPage}
        >
          {allEligibleStudentsOnPageSelected ? 'Clear page' : 'Select page'}
        </button>
      </div>
    ) : null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add participant"
      description="Browse or search members and students, or register a guest walk-in."
      width="lg"
      footer={
        activeTab === 'guest' ? (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="play-event-add-guest-form" isLoading={submitting}>
              Add guest
            </Button>
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {selectedBulkCount > 0 ? (
              <Button
                type="button"
                className="mr-auto"
                isLoading={submitting}
                disabled={bulkAddEligibleCount === 0}
                title={
                  bulkAddEligibleCount === 0
                    ? 'Selected members need gender on file — add them individually'
                    : undefined
                }
                onClick={() =>
                  void (activeTab === 'member' ? handleBulkAddMembers() : handleBulkAddStudents())
                }
              >
                <UserPlus className="mr-1.5 h-4 w-4" aria-hidden />
                Add selected ({selectedBulkCount})
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {allowedTabs.length > 1 ? (
          <DashboardTabNav<ParticipantTab>
            role="tablist"
            tabs={participantTabs}
            activeTab={activeTab}
            onChange={handleTabChange}
            ariaLabel="Participant type"
            withMargin={false}
          />
        ) : null}

        {showCompedToggle ? (
          <div className="rounded-lg border border-border bg-muted/20 px-3 py-3">
            <label className="flex cursor-pointer items-start gap-2.5">
              <Checkbox
                checked={comped}
                className="mt-0.5"
                onCheckedChange={(v) => setComped(v === true)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  Comp entry
                </span>
                <span className="block text-xs text-muted-foreground">
                  No payment due for this participant.
                </span>
              </span>
            </label>
          </div>
        ) : null}

        {activeTab === 'member' || activeTab === 'student' ? (
          <section className="space-y-4">
            <div>
              <label
                htmlFor="play-participant-search"
                className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
              >
                {activeTab === 'member' ? 'Find member' : 'Find student'}
              </label>
              <div className="relative">
                <Input
                  id="play-participant-search"
                  type="search"
                  autoComplete="off"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={activeTab === 'member' ? 'Search club members…' : 'Search students…'}
                  className="pr-10"
                />
                {browseLoading ? (
                  <Loader2
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Search runs on the server as you type.
              </p>
            </div>

            {activeTab === 'student' && !academyId ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No academy linked to this organization — add academy students from an academy-enabled org.
              </p>
            ) : activeTab === 'member' ? (
              <>
                <ParticipantBrowsePanel
                  title="Club members"
                  rows={memberRows}
                  pagination={memberPagination}
                  loading={memberListLoading}
                  onPageChange={setMemberPage}
                  emptyMessage={
                    searchQuery
                      ? 'No members match that search.'
                      : 'No active club members found.'
                  }
                  renderRow={renderMemberRow}
                  getRowKey={(member) => member.id}
                  bulkToolbar={memberBulkToolbar}
                />

                {pendingMember ? (
                  <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
                    <div>
                      <p className="font-medium">
                        {pendingMember.firstName} {pendingMember.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Gender is required before adding this member.
                      </p>
                    </div>
                    <Field>
                      <FieldLabel htmlFor="play-pending-member-gender">Gender</FieldLabel>
                      <Select
                        value={pendingMemberGender || undefined}
                        onValueChange={(value) => setPendingMemberGender(value as ParticipantGender)}
                      >
                        <SelectTrigger id="play-pending-member-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setPendingMember(null);
                          setPendingMemberGender('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        isLoading={submitting}
                        onClick={handleConfirmPendingMember}
                      >
                        Confirm add
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <ParticipantBrowsePanel
                title="Academy students"
                rows={studentRows}
                pagination={studentPagination}
                loading={studentListLoading}
                onPageChange={setStudentPage}
                emptyMessage={
                  searchQuery
                    ? 'No students match that search.'
                    : 'No active academy students found.'
                }
                renderRow={renderStudentRow}
                getRowKey={(student) => student.id}
                bulkToolbar={studentBulkToolbar}
              />
            )}
          </section>
        ) : (
          <form id="play-event-add-guest-form" className="space-y-4" onSubmit={(e) => void handleAddGuest(e)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="play-guest-first">First name</FieldLabel>
                <Input
                  id="play-guest-first"
                  value={guestFirstName}
                  onChange={(e) => setGuestFirstName(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="play-guest-last">Last name</FieldLabel>
                <Input
                  id="play-guest-last"
                  value={guestLastName}
                  onChange={(e) => setGuestLastName(e.target.value)}
                  required
                />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="play-guest-email">Email</FieldLabel>
              <Input
                id="play-guest-email"
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="Required if no phone"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-guest-phone">Phone</FieldLabel>
              <Input
                id="play-guest-phone"
                type="tel"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                placeholder="Required if no email"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-guest-gender">Gender</FieldLabel>
              <Select
                value={guestGender || undefined}
                onValueChange={(value) => setGuestGender(value as ParticipantGender)}
              >
                <SelectTrigger id="play-guest-gender">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {ratingsEnabled ? (
              <Field>
                <FieldLabel htmlFor="play-guest-rating">
                  {ratingSettings?.label ?? 'Player rating'} (optional)
                </FieldLabel>
                <Input
                  id="play-guest-rating"
                  type="number"
                  step={ratingSettings?.step ?? 0.5}
                  min={ratingSettings?.min}
                  max={ratingSettings?.max}
                  value={guestPlayerRating}
                  onChange={(e) => setGuestPlayerRating(e.target.value)}
                  placeholder={`${ratingSettings?.min ?? 1}–${ratingSettings?.max ?? 16.5}`}
                />
                <FieldDescription>
                  Used for pairing and seeding on this event only.
                </FieldDescription>
              </Field>
            ) : null}
          </form>
        )}
      </div>
    </Drawer>
  );
}
